<template>
  <div class="attendance-stat-v3">
    <!-- Header Section -->
    <div class="stat-section-header">
      <div class="header-title-group">
        <div class="header-icon-badge">
          <i class="el-icon-user-solid"></i>
        </div>
        <div>
          <h2 class="header-title">Thống kê điểm danh & Chuyên cần học sinh</h2>
          <p class="header-subtitle">Tổng hợp trạng thái học sinh, điểm danh đi học và các suất ăn trong ngày</p>
        </div>
      </div>

      <!-- Bộ lọc ngày chuyên dụng -->
      <div class="header-filters">
        <div class="date-filter-group">
          <button
            type="button"
            class="date-nav-btn"
            title="Ngày trước"
            @click="changeSelectedDate(-1)"
          >
            <i class="el-icon-arrow-left"></i>
          </button>

          <el-date-picker
            v-model="selectedDate"
            type="date"
            size="small"
            format="dd/MM/yyyy"
            value-format="yyyy-MM-dd"
            placeholder="Chọn ngày"
            :clearable="false"
            :picker-options="datePickerOptions"
            class="attendance-date-picker"
            @change="handleDateChange"
          />

          <button
            type="button"
            class="date-current-btn"
            @click="setToday"
          >
            Hôm nay
          </button>

          <button
            type="button"
            class="date-nav-btn"
            title="Ngày sau"
            @click="changeSelectedDate(1)"
          >
            <i class="el-icon-arrow-right"></i>
          </button>
        </div>

        <div class="header-badge-group">
          <span class="live-indicator">
            <span class="pulse-dot"></span> {{ dayOfWeekLabel }}, <strong>{{ formattedSelectedDate }}</strong>
          </span>
        </div>
      </div>
    </div>

    <el-alert v-if="apiError" :title="apiError" type="error" :closable="false" show-icon class="stat-api-error" />
    <el-alert
      v-if="!isToday"
      title="Số liệu lịch sử cần đối chiếu trước khi sử dụng làm báo cáo chính thức."
      type="warning"
      :closable="false"
      show-icon
      class="stat-api-error"
    />

    <!-- 1. Cụm chỉ số chính: Tình trạng học sinh & Chuyên cần -->
    <div class="kpi-sections-wrapper">
      <!-- Nhóm 1: Tình Trạng Hồ Sơ Học Sinh -->
      <div class="metric-block">
        <div class="metric-block-title">
          <i class="el-icon-s-custom text-indigo"></i>
          <span>Hồ Sơ Học Sinh</span>
        </div>
        <div class="metric-grid" v-loading="loadingSummary">
          <div class="metric-card border-indigo">
            <span class="metric-label">Tổng số học sinh</span>
            <div class="metric-val text-indigo">{{ summary.totalStudents }}</div>
            <div class="metric-sub">Toàn bộ trường</div>
          </div>
          <div class="metric-card border-green">
            <span class="metric-label">Đang học</span>
            <div class="metric-val text-green">{{ summary.studyingStudents }}</div>
            <div class="metric-sub">{{ summary.studyingRate }}% tổng số</div>
          </div>
          <div class="metric-card border-amber">
            <span class="metric-label">Bảo lưu</span>
            <div class="metric-val text-amber">{{ summary.reservedStudents }}</div>
            <div class="metric-sub">Tạm nghỉ học</div>
          </div>
          <div class="metric-card border-blue">
            <span class="metric-label">Chờ học</span>
            <div class="metric-val text-blue">{{ summary.waitingStudents }}</div>
            <div class="metric-sub">Hồ sơ mới</div>
          </div>
        </div>
      </div>

      <!-- Nhóm 2: Tình Trạng Điểm Danh Đi Học Hôm Nay -->
      <div class="metric-block">
        <div class="metric-block-title">
          <i class="el-icon-date text-emerald"></i>
          <span>Điểm Danh Đi Học</span>
        </div>
        <div class="metric-grid" v-loading="loadingSummary">
          <div class="metric-card border-emerald">
            <span class="metric-label">Đi học</span>
            <div class="metric-val text-emerald">{{ summary.present }}</div>
            <div class="metric-sub">{{ summary.attendanceRate }}% chuyên cần</div>
          </div>
          <div class="metric-card border-yellow">
            <span class="metric-label">Nghỉ có phép</span>
            <div class="metric-val text-yellow">{{ summary.absentPermitted }}</div>
            <div class="metric-sub">Có đơn xin phép</div>
          </div>
          <div class="metric-card border-rose">
            <span class="metric-label">Nghỉ không phép</span>
            <div class="metric-val text-rose">{{ summary.absentUnpermitted }}</div>
            <div class="metric-sub">Chưa rõ lý do</div>
          </div>
          <div class="metric-card border-slate">
            <span class="metric-label">Chưa điểm danh</span>
            <div class="metric-val text-slate">{{ summary.unmarked }}</div>
            <div class="metric-sub">Chờ giáo viên điểm danh</div>
          </div>
        </div>
      </div>

      <!-- Nhóm 3: Chỉ Số Điểm Danh Ăn 4 Bữa -->
      <div class="metric-block">
        <div class="metric-block-title">
          <i class="el-icon-dish text-orange"></i>
          <span>Điểm Danh Suất Ăn</span>
        </div>
        <div class="metric-grid" v-loading="loadingSummary">
          <div class="metric-card border-orange">
            <span class="metric-label">Bữa sáng</span>
            <div class="metric-val text-orange">{{ summary.breakfast }}</div>
            <div class="metric-sub">{{ summary.breakfastRate }}% HS đi học</div>
          </div>
          <div class="metric-card border-teal">
            <span class="metric-label">Bữa chính</span>
            <div class="metric-val text-teal">{{ summary.mainMeal }}</div>
            <div class="metric-sub">{{ summary.mainMealRate }}% HS đi học</div>
          </div>
          <div class="metric-card border-purple">
            <span class="metric-label">Bữa phụ</span>
            <div class="metric-val text-purple">{{ summary.snack }}</div>
            <div class="metric-sub">{{ summary.snackRate }}% HS đi học</div>
          </div>
          <div class="metric-card border-dark">
            <span class="metric-label">Bữa tối</span>
            <div class="metric-val text-dark">{{ summary.dinner }}</div>
            <div class="metric-sub">Học sinh đăng ký muộn</div>
          </div>
        </div>
      </div>

      <!-- Nhóm 4: Tương Tác Phụ Huynh & Đón Trả -->
      <div class="metric-block">
        <div class="metric-block-title">
          <i class="el-icon-chat-line-square text-cyan"></i>
          <span>Tương Tác & Đón Trả</span>
        </div>
        <div class="metric-grid">
          <div class="metric-card border-cyan" v-loading="loadingSummary">
            <span class="metric-label">Lời nhắn</span>
            <div class="metric-val text-cyan">{{ summary.parentMessages }}</div>
            <div class="metric-sub">Phụ huynh gửi trong ngày</div>
          </div>
          <div class="metric-card border-pink" v-loading="loadingSummary">
            <span class="metric-label">Dặn thuốc</span>
            <div class="metric-val text-pink">{{ summary.medicineOrders }}</div>
            <div class="metric-sub">Đơn thuốc gửi cô</div>
          </div>
          <div class="metric-card border-red">
            <span class="metric-label">Đơn chưa duyệt</span>
            <div class="metric-val text-red">{{ pendingAbsentRequests.pendingCount }}</div>
            <div class="metric-sub">Đơn nghỉ chờ duyệt · Hiện tại</div>
          </div>
          <div class="metric-card border-violet" v-loading="loadingSummary">
            <span class="metric-label">Đón muộn</span>
            <div class="metric-val text-violet">{{ summary.latePickups }}</div>
            <div class="metric-sub">Học sinh đã được đón muộn</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. Khu vực Biểu đồ trực quan -->
    <el-row :gutter="20" class="charts-row">
      <!-- Biểu đồ 1: Tỷ lệ đi học hôm nay (Gauge) -->
      <el-col :xs="24" :sm="24" :md="8" :lg="7">
        <div class="chart-card h-100" v-loading="loadingSummary">
          <div class="chart-card-head">
            <div>
              <h3 class="chart-title">Tỷ Lệ Đi Học</h3>
              <p class="chart-subtitle">So với tổng số học sinh đang học ({{ summary.studyingStudents }} HS)</p>
            </div>
            <span class="tag-pill tag-pill--green">{{ isToday ? "Hôm nay" : formattedSelectedDate }}</span>
          </div>

          <div class="chart-container" ref="chartAttendanceGauge"></div>

          <div class="gauge-footer-stats">
            <div class="gauge-stat-box">
              <span class="g-label">Có mặt đi học</span>
              <strong class="g-val text-emerald">{{ summary.present }} HS</strong>
            </div>
            <div class="gauge-stat-box">
              <span class="g-label">Vắng mặt</span>
              <strong class="g-val text-rose">{{ summary.absentTotal }} HS</strong>
            </div>
            <div class="gauge-stat-box">
              <span class="g-label">Chưa điểm danh</span>
              <strong class="g-val text-slate">{{ summary.unmarked }} HS</strong>
            </div>
          </div>
        </div>
      </el-col>

      <!-- Biểu đồ 2: Xu hướng chuyên cần & Tỷ lệ so với tổng số học sinh (7 ngày qua, Tuần, Tháng) -->
      <el-col :xs="24" :sm="24" :md="16" :lg="10">
        <div class="chart-card h-100">
          <div class="chart-card-head">
            <div>
              <h3 class="chart-title">Xu Hướng Chuyên Cần & Tổng Học Sinh</h3>
              <p class="chart-subtitle">Thể hiện số lượng đi học và tỷ lệ % so với tổng số học sinh</p>
            </div>

            <!-- Time Scope Switcher -->
            <div class="scope-toggle-tabs">
              <button
                v-for="scope in scopeOptions"
                :key="scope.value"
                :class="['scope-btn', { active: currentTrendScope === scope.value }]"
                @click="changeTrendScope(scope.value)"
              >
                {{ scope.label }}
              </button>
            </div>
          </div>

          <div class="chart-container" ref="chartAttendanceTrend" v-loading="loadingTrend"></div>

          <div class="trend-legend-bar">
            <div class="legend-item">
              <span class="legend-color-box bar-color"></span>
              <span>Số học sinh đi học</span>
            </div>
            <div class="legend-item">
              <span class="legend-color-box bg-slate-color"></span>
              <span>Tổng số học sinh</span>
            </div>
            <div class="legend-item">
              <span class="legend-color-box line-color"></span>
              <span>Tỷ lệ đi học (%)</span>
            </div>
          </div>
        </div>
      </el-col>

      <!-- Biểu đồ 3: Điểm danh theo suất ăn (Sáng, Chính, Phụ, Tối) -->
      <el-col :xs="24" :sm="24" :md="24" :lg="7">
        <div class="chart-card h-100" v-loading="loadingSummary">
          <div class="chart-card-head">
            <div>
              <h3 class="chart-title">Điểm Danh Theo Suất Ăn</h3>
              <p class="chart-subtitle">Thống kê 4 bữa: Sáng, Chính, Phụ, Tối</p>
            </div>
            <span class="tag-pill tag-pill--orange">4 bữa ăn</span>
          </div>

          <div class="chart-container chart-container--meal" ref="chartMealStats"></div>

          <div class="meal-detail-list">
            <div class="meal-detail-item">
              <div class="meal-name"><i class="el-icon-sunrise text-orange"></i> Bữa Sáng:</div>
              <div class="meal-counts">
                <b>{{ summary.breakfast }}</b> / {{ summary.present }} HS
                <span class="meal-rate text-orange">({{ summary.breakfastRate }}%)</span>
              </div>
            </div>
            <div class="meal-detail-item">
              <div class="meal-name"><i class="el-icon-food text-teal"></i> Bữa Chính (Trưa):</div>
              <div class="meal-counts">
                <b>{{ summary.mainMeal }}</b> / {{ summary.present }} HS
                <span class="meal-rate text-teal">({{ summary.mainMealRate }}%)</span>
              </div>
            </div>
            <div class="meal-detail-item">
              <div class="meal-name"><i class="el-icon-coffee-cup text-purple"></i> Bữa Phụ (Xế):</div>
              <div class="meal-counts">
                <b>{{ summary.snack }}</b> / {{ summary.present }} HS
                <span class="meal-rate text-purple">({{ summary.snackRate }}%)</span>
              </div>
            </div>
            <div class="meal-detail-item">
              <div class="meal-name"><i class="el-icon-moon text-dark"></i> Bữa Tối:</div>
              <div class="meal-counts">
                <b>{{ summary.dinner }}</b> / {{ summary.present }} HS
                <span class="meal-rate text-dark">({{ summary.dinnerRate }}%)</span>
              </div>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 3. BẢNG THỐNG KÊ CHỈ SỐ ĐIỂM DANH TỪNG LỚP HỌC -->
    <div class="class-attendance-section">
      <div :class="['class-attendance-head', { 'is-expanded': showClassTable }]">
        <div class="class-attendance-title-group">
          <div class="section-icon-badge">
            <i class="el-icon-s-grid"></i>
          </div>
          <div>
            <h3 class="section-heading">Chỉ Số Điểm Danh Từng Lớp Học</h3>
            <p class="section-subheading">
              Theo dõi chi tiết sĩ số, tiến độ điểm danh đến, chuyên cần đi học, nghỉ học và các suất ăn theo lớp
            </p>
          </div>
        </div>

        <div class="class-attendance-actions">
          <span class="class-counter-badge">
            <i class="el-icon-school"></i> {{ filteredClassAttendanceList.length }} Lớp
          </span>
          <el-button
            size="small"
            type="primary"
            plain
            class="table-toggle-btn"
            @click="showClassTable = !showClassTable"
          >
            <i :class="showClassTable ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
            {{ showClassTable ? 'Thu gọn bảng' : 'Xem chi tiết bảng' }}
          </el-button>
        </div>
      </div>

      <el-collapse-transition>
        <div v-show="showClassTable" class="class-table-collapsible-body">
          <div class="class-attendance-filter-bar">
            <!-- Bộ lọc Khối -->
            <div class="filter-chip">
              <span class="chip-label"><i class="el-icon-collection-tag"></i> Khối:</span>
              <el-select
                v-model="selectedTableGrade"
                size="small"
                placeholder="Tất cả các khối"
                class="grade-select"
              >
                <el-option
                  v-for="g in gradeOptions"
                  :key="g.value"
                  :label="g.label"
                  :value="g.value"
                />
              </el-select>
            </div>

            <!-- Ô tìm kiếm tên lớp -->
            <div class="search-box">
              <el-input
                v-model="tableSearchQuery"
                size="small"
                placeholder="Tìm theo tên lớp..."
                prefix-icon="el-icon-search"
                clearable
                class="class-search-input"
              />
            </div>
          </div>

          <el-alert v-if="classesError" :title="classesError" type="error" :closable="false" show-icon class="stat-api-error" />

          <!-- Bảng thống kê chi tiết từng lớp -->
          <div class="class-attendance-table-wrap" v-loading="loadingClasses">
        <el-table
          :data="filteredClassAttendanceList"
          stripe
          border
          size="small"
          max-height="500"
          class="modern-class-attendance-table"
          empty-text="Chưa có dữ liệu điểm danh theo lớp"
        >
          <el-table-column type="index" label="STT" width="55" align="center" />

          <!-- Tên Lớp & Khối -->
          <el-table-column label="Lớp Học & Khối" min-width="170">
            <template slot-scope="scope">
              <div class="class-meta-cell">
                <strong class="class-name-txt">{{ scope.row.className }}</strong>
                <span class="grade-pill" :class="'pill-' + scope.row.gradeId">{{ scope.row.gradeName }}</span>
              </div>
            </template>
          </el-table-column>

          <!-- Sĩ Số Lớp -->
          <el-table-column label="Sĩ Số" width="90" align="center">
            <template slot-scope="scope">
              <span class="badge-total-students">{{ scope.row.kidsTotalNumber }} HS</span>
            </template>
          </el-table-column>

          <!-- Tiến Độ Điểm Danh Đến (Đã điểm danh vs Chưa điểm danh) -->
          <el-table-column label="Tiến Độ Điểm Danh" width="155" align="center">
            <template slot-scope="scope">
              <div class="attendance-progress-cell">
                <span class="badge-done" v-if="scope.row.attendanceNo === 0">
                  <i class="el-icon-circle-check"></i> Đã điểm danh ({{ scope.row.attendanceYes }})
                </span>
                <span class="badge-pending" v-else>
                  <i class="el-icon-warning-outline"></i> Còn {{ scope.row.attendanceNo }} HS
                </span>
              </div>
            </template>
          </el-table-column>

          <!-- Đi Học & Tỷ Lệ Chuyên Cần -->
          <el-table-column label="Đi Học (Chuyên Cần)" min-width="175" align="center">
            <template slot-scope="scope">
              <div class="rate-progress-cell">
                <div class="rate-info-row">
                  <strong class="text-emerald">{{ scope.row.goSchoolNumber }} HS</strong>
                  <span class="rate-pct-tag" :class="getRateColorClass(scope.row)">
                    {{ scope.row.attendanceRate }}%
                  </span>
                </div>
                <div class="progress-bar-bg">
                  <div
                    class="progress-bar-fill"
                    :style="{
                      width: scope.row.attendanceRate + '%',
                      backgroundColor: getRateColor(scope.row)
                    }"
                  ></div>
                </div>
              </div>
            </template>
          </el-table-column>

          <!-- Nghỉ Học (Có Phép / Không Phép) -->
          <el-table-column label="Nghỉ Học" width="150" align="center">
            <template slot-scope="scope">
              <div class="absent-stat-cell">
                <span class="stat-permit" title="Nghỉ có phép">
                  <i class="el-icon-document-checked"></i> Có phép: <b>{{ scope.row.absentWithPermit }}</b>
                </span>
                <span class="stat-no-permit" title="Nghỉ không phép">
                  <i class="el-icon-circle-close"></i> K.phép: <b>{{ scope.row.absentNoPermit }}</b>
                </span>
              </div>
            </template>
          </el-table-column>

          <!-- Điểm Danh Suất Ăn -->
          <el-table-column label="Báo Ăn (Bữa)" width="185" align="center">
            <template slot-scope="scope">
              <div class="meal-mini-chips">
                <span class="chip-m chip-breakfast" title="Bữa sáng">
                  <i class="el-icon-sunrise"></i> S: {{ scope.row.mealBreakfast }}
                </span>
                <span class="chip-m chip-lunch" title="Bữa trưa">
                  <i class="el-icon-food"></i> T: {{ scope.row.mealLunch }}
                </span>
                <span class="chip-m chip-snack" title="Bữa xế">
                  <i class="el-icon-coffee-cup"></i> X: {{ scope.row.mealSnack }}
                </span>
                <span class="chip-m chip-dinner" title="Bữa tối">
                  <i class="el-icon-moon"></i> Tối: {{ scope.row.mealDinner }}
                </span>
              </div>
            </template>
          </el-table-column>

          <!-- Đón Về -->
          <el-table-column label="Đón Về" width="130" align="center">
            <template slot-scope="scope">
              <div class="leave-stat-cell">
                <span class="leave-yes"><i class="el-icon-check"></i> Đã về: {{ scope.row.leaveYes }}</span>
                <span class="leave-no"><i class="el-icon-time"></i> Chưa về: {{ scope.row.leaveNo }}</span>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
        </div>
      </el-collapse-transition>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";
import moment from "moment";
import AttendanceStatisticsService from "@/services/dashboard_v3/AttendanceStatisticsService";

const todayInSchoolZone = () => moment().utcOffset(420).format("YYYY-MM-DD");

const emptySummary = () => ({
  date: null,
  totalStudents: 0,
  studyingStudents: 0,
  reservedStudents: 0,
  waitingStudents: 0,
  studyingRate: 0,
  present: 0,
  absentPermitted: 0,
  absentUnpermitted: 0,
  absentTotal: 0,
  unmarked: 0,
  attendanceRate: 0,
  breakfast: 0,
  breakfastRate: 0,
  mainMeal: 0,
  mainMealRate: 0,
  snack: 0,
  snackRate: 0,
  dinner: 0,
  dinnerRate: 0,
  parentMessages: 0,
  medicineOrders: 0,
  latePickups: 0
});

const emptyTrend = () => ({ mode: "SEVEN_DAYS", startDate: null, endDate: null, points: [] });

export default {
  name: "AttendanceStatisticsV3",
  props: {
    autoLoad: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      hasLoadedData: false,
      isLoadingData: false,
      selectedDate: todayInSchoolZone(),
      datePickerOptions: {
        disabledDate: date => moment(date).isAfter(moment(todayInSchoolZone()), "day")
      },
      currentTrendScope: "SEVEN_DAYS",
      scopeOptions: [
        { label: "7 ngày qua", value: "SEVEN_DAYS" },
        { label: "Theo tuần", value: "WEEK" },
        { label: "Theo tháng", value: "MONTH" }
      ],
      summary: emptySummary(),
      trend: emptyTrend(),
      pendingAbsentRequests: { pendingCount: 0, asOf: null },
      loadingSummary: false,
      loadingTrend: false,
      loadingClasses: false,
      summaryError: "",
      trendError: "",
      pendingError: "",
      classesError: "",
      summaryRequestId: 0,
      trendRequestId: 0,
      pendingRequestId: 0,
      classesRequestId: 0,
      chartGauge: null,
      chartTrend: null,
      chartMeal: null,
      selectedTableGrade: "",
      tableSearchQuery: "",
      classAttendanceList: [],
      showClassTable: false
    };
  },
  computed: {
    formattedSelectedDate() {
      return moment(this.selectedDate).format("DD/MM/YYYY");
    },
    dayOfWeekLabel() {
      const days = ["Chủ nhật", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
      return days[moment(this.selectedDate).day()] || "";
    },
    isToday() {
      return this.selectedDate === todayInSchoolZone();
    },
    apiError() {
      return [this.summaryError, this.trendError, this.pendingError].filter(Boolean).join(" ");
    },
    gradeOptions() {
      const grades = new Map();
      this.classAttendanceList.forEach(row => grades.set(row.gradeId, row.gradeName));
      return [{ label: "Tất cả các khối", value: "" },
        ...Array.from(grades, ([value, label]) => ({ value, label }))];
    },
    filteredClassAttendanceList() {
      let list = this.classAttendanceList;
      if (this.selectedTableGrade) list = list.filter(c => c.gradeId === this.selectedTableGrade);
      if (this.tableSearchQuery) {
        const query = this.tableSearchQuery.toLowerCase();
        list = list.filter(c => c.className.toLowerCase().includes(query) || c.gradeName.toLowerCase().includes(query));
      }
      return list;
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initCharts();
      window.addEventListener("resize", this.handleResize);
      if (this.autoLoad) {
        this.loadData();
      }
    });
  },
  beforeDestroy() {
    this.summaryRequestId++;
    this.trendRequestId++;
    this.pendingRequestId++;
    this.classesRequestId++;
    window.removeEventListener("resize", this.handleResize);
    this.disposeCharts();
  },
  methods: {
    async loadData() {
      if (this.hasLoadedData || this.isLoadingData) return;
      this.isLoadingData = true;
      try {
        await this.loadForDate();
        await this.loadPendingAbsentRequests();
        this.hasLoadedData = true;
      } finally {
        this.isLoadingData = false;
      }
    },
    changeSelectedDate(step) {
      const next = moment(this.selectedDate).add(step, "days").format("YYYY-MM-DD");
      if (next > todayInSchoolZone()) return;
      this.selectedDate = next;
      this.handleDateChange();
    },
    setToday() {
      this.selectedDate = todayInSchoolZone();
      this.handleDateChange();
    },
    handleDateChange() {
      if (this.selectedDate) this.loadForDate();
    },
    async loadForDate() {
      await this.loadSummary();
      await this.loadTrend();
      await this.loadClasses();
    },
    async loadClasses() {
      const requestId = ++this.classesRequestId;
      this.loadingClasses = true;
      this.classesError = "";
      try {
        const response = await AttendanceStatisticsService.getClasses(this.selectedDate);
        if (requestId !== this.classesRequestId) return;
        if (!response.data || !Array.isArray(response.data.data)) throw new Error("Empty classes response");
        this.classAttendanceList = response.data.data;
        if (!this.gradeOptions.some(grade => grade.value === this.selectedTableGrade)) {
          this.selectedTableGrade = "";
        }
      } catch (error) {
        if (requestId !== this.classesRequestId) return;
        this.classAttendanceList = [];
        this.classesError = "Không tải được điểm danh từng lớp.";
      } finally {
        if (requestId === this.classesRequestId) this.loadingClasses = false;
      }
    },
    async loadSummary() {
      const requestId = ++this.summaryRequestId;
      const date = this.selectedDate;
      this.loadingSummary = true;
      this.summaryError = "";
      try {
        const response = await AttendanceStatisticsService.getSummary(date);
        if (requestId !== this.summaryRequestId) return;
        if (!response.data || !response.data.data) throw new Error("Empty summary response");
        this.summary = response.data.data;
      } catch (error) {
        if (requestId !== this.summaryRequestId) return;
        this.summary = emptySummary();
        this.summaryError = "Không tải được số liệu điểm danh.";
      } finally {
        if (requestId === this.summaryRequestId) {
          this.loadingSummary = false;
          this.$nextTick(() => {
            this.renderGaugeChart();
            this.renderMealChart();
          });
        }
      }
    },
    async loadTrend() {
      const requestId = ++this.trendRequestId;
      const date = this.selectedDate;
      const mode = this.currentTrendScope;
      this.loadingTrend = true;
      this.trendError = "";
      try {
        const response = await AttendanceStatisticsService.getTrend(date, mode);
        if (requestId !== this.trendRequestId) return;
        if (!response.data || !response.data.data) throw new Error("Empty trend response");
        this.trend = response.data.data;
      } catch (error) {
        if (requestId !== this.trendRequestId) return;
        this.trend = emptyTrend();
        this.trendError = "Không tải được xu hướng chuyên cần.";
      } finally {
        if (requestId === this.trendRequestId) {
          this.loadingTrend = false;
          this.$nextTick(this.renderTrendChart);
        }
      }
    },
    async loadPendingAbsentRequests() {
      const requestId = ++this.pendingRequestId;
      this.pendingError = "";
      try {
        const response = await AttendanceStatisticsService.getPendingAbsentRequests();
        if (requestId !== this.pendingRequestId) return;
        if (!response.data || !response.data.data) throw new Error("Empty pending response");
        this.pendingAbsentRequests = response.data.data;
      } catch (error) {
        if (requestId !== this.pendingRequestId) return;
        this.pendingAbsentRequests = { pendingCount: 0, asOf: null };
        this.pendingError = "Không tải được số đơn nghỉ chờ duyệt.";
      }
    },
    changeTrendScope(scope) {
      if (scope === this.currentTrendScope) return;
      this.currentTrendScope = scope;
      this.loadTrend();
    },
    handleResize() {
      if (this.chartGauge) this.chartGauge.resize();
      if (this.chartTrend) this.chartTrend.resize();
      if (this.chartMeal) this.chartMeal.resize();
    },
    disposeCharts() {
      if (this.chartGauge) this.chartGauge.dispose();
      if (this.chartTrend) this.chartTrend.dispose();
      if (this.chartMeal) this.chartMeal.dispose();
      this.chartGauge = null;
      this.chartTrend = null;
      this.chartMeal = null;
    },
    initCharts() {
      this.renderGaugeChart();
      this.renderTrendChart();
      this.renderMealChart();
    },
    renderGaugeChart() {
      const dom = this.$refs.chartAttendanceGauge;
      if (!dom) return;
      if (!this.chartGauge) this.chartGauge = echarts.init(dom);
      this.chartGauge.setOption({
        series: [{
          type: "gauge", startAngle: 210, endAngle: -30, min: 0, max: 100,
          itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: "#38bdf8" }, { offset: 0.6, color: "#3b82f6" }, { offset: 1, color: "#10b981" }
          ]) },
          progress: { show: true, roundCap: true, width: 14 },
          pointer: { show: false },
          axisLine: { roundCap: true, lineStyle: { width: 14, color: [[1, "#f1f5f9"]] } },
          axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false },
          title: { show: true, offsetCenter: [0, "20%"], fontSize: 13, color: "#64748b", fontWeight: 600 },
          detail: { valueAnimation: true, offsetCenter: [0, "-15%"], fontSize: 34, fontWeight: 800,
            formatter: "{value}%", color: "#0f172a" },
          data: [{ value: Number(this.summary.attendanceRate) || 0, name: "Tỷ lệ chuyên cần" }]
        }]
      });
    },
    renderTrendChart() {
      const dom = this.$refs.chartAttendanceTrend;
      if (!dom) return;
      if (!this.chartTrend) this.chartTrend = echarts.init(dom);
      const points = this.trend.points || [];
      const labels = points.map(point => point.label || moment(point.date).format("DD/MM"));
      const rates = points.map(point => Number(point.attendanceRate));
      const minRate = rates.length ? Math.min(...rates) : 80;
      this.chartTrend.setOption({
        tooltip: {
          trigger: "axis", backgroundColor: "rgba(15, 23, 42, 0.9)", borderColor: "transparent",
          textStyle: { color: "#fff" },
          formatter: params => {
            const point = points[params[0].dataIndex];
            if (!point) return "";
            const period = point.periodStart === point.periodEnd
              ? moment(point.date).format("DD/MM/YYYY")
              : `${moment(point.periodStart).format("DD/MM/YYYY")} – ${moment(point.periodEnd).format("DD/MM/YYYY")}`;
            const unit = this.currentTrendScope === "SEVEN_DAYS" ? "" : " (trung bình/ngày)";
            return `<b>${point.label || period}</b><br/>${period}<br/>Đi học${unit}: ${point.present} HS` +
              `<br/>Đang học${unit}: ${point.studyingStudents} HS<br/>Tỷ lệ: ${point.attendanceRate}%` +
              (this.currentTrendScope === "SEVEN_DAYS" ? "" : `<br/>Số ngày học: ${point.sampleDays}`);
          }
        },
        grid: { top: 35, left: 10, right: 15, bottom: 10, containLabel: true },
        xAxis: { type: "category", data: labels, axisLine: { lineStyle: { color: "#cbd5e1" } },
          axisTick: { show: false }, axisLabel: { color: "#475569", fontWeight: 600, fontSize: 11 } },
        yAxis: [
          { type: "value", name: "Học sinh", min: 0, splitLine: { lineStyle: { color: "#f1f5f9", type: "dashed" } } },
          { type: "value", name: "Tỷ lệ (%)", min: Math.min(80, Math.floor(minRate / 10) * 10),
            max: 100, splitLine: { show: false },
            axisLabel: { formatter: "{value}%" } }
        ],
        series: [
          { name: "Đang học", type: "bar", barWidth: 18, barGap: "-100%",
            data: points.map(point => point.studyingStudents),
            itemStyle: { color: "#e2e8f0", borderRadius: [6, 6, 0, 0] } },
          { name: "Đi học", type: "bar", barWidth: 18, data: points.map(point => point.present),
            itemStyle: { color: "#2563eb", borderRadius: [6, 6, 0, 0] } },
          { name: "Tỷ lệ đi học", type: "line", yAxisIndex: 1, smooth: true, symbolSize: 8,
            data: points.map(point => Number(point.attendanceRate)),
            lineStyle: { width: 3, color: "#10b981" }, itemStyle: { color: "#10b981" } }
        ]
      }, true);
    },
    renderMealChart() {
      const dom = this.$refs.chartMealStats;
      if (!dom) return;
      if (!this.chartMeal) this.chartMeal = echarts.init(dom);
      const counts = [this.summary.breakfast, this.summary.mainMeal, this.summary.snack, this.summary.dinner];
      const rates = [this.summary.breakfastRate, this.summary.mainMealRate,
        this.summary.snackRate, this.summary.dinnerRate];
      const colors = ["#f97316", "#14b8a6", "#9333ea", "#475569"];
      this.chartMeal.setOption({
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" },
          formatter: params => {
            const item = params[0];
            return `<b>${item.name}</b><br/>Số suất ăn: <b>${item.value}</b> (${rates[item.dataIndex]}% HS đi học)`;
          } },
        grid: { top: 25, left: 10, right: 15, bottom: 10, containLabel: true },
        xAxis: { type: "category", data: ["Sáng", "Chính", "Phụ", "Tối"], axisTick: { show: false } },
        yAxis: { type: "value", splitLine: { lineStyle: { color: "#f8fafc" } } },
        series: [{ type: "bar", barWidth: 26,
          data: counts.map((count, index) => ({ value: count,
            itemStyle: { color: colors[index], borderRadius: [6, 6, 0, 0] } })),
          label: { show: true, position: "top", color: "#1e293b", fontWeight: 800, fontSize: 11 } }]
      });
    },
    getRateColorClass(row) {
      const rate = Number(row.attendanceRate);
      if (rate >= 95) return "rate-good";
      if (rate >= 88) return "rate-mid";
      return "rate-low";
    },
    getRateColor(row) {
      const rate = Number(row.attendanceRate);
      if (rate >= 95) return "#10b981";
      if (rate >= 88) return "#3b82f6";
      return "#f59e0b";
    }
  }
};
</script>

<style scoped>
.attendance-stat-v3 {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px -3px rgba(0, 0, 0, 0.04);
  margin-bottom: 28px;
}

/* Header */
.stat-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 22px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.header-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #e0e7ff;
  color: #4f46e5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.header-title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.header-subtitle {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

/* Header Date Filters */
.header-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.date-filter-group {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  padding: 3px 4px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.date-nav-btn {
  border: none;
  background: transparent;
  color: #64748b;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: all 0.2s ease;
}

.date-nav-btn:hover {
  background: #ffffff;
  color: #2563eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.date-current-btn {
  border: none;
  background: transparent;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  padding: 0 8px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.date-current-btn:hover {
  background: #ffffff;
  color: #0f172a;
}

.attendance-date-picker {
  width: 140px !important;
}

.attendance-date-picker >>> .el-input__inner {
  border: none !important;
  background: transparent !important;
  font-weight: 600 !important;
  color: #0f172a !important;
  padding-left: 26px !important;
  padding-right: 0 !important;
}

.live-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #059669;
  background: #ecfdf5;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid #a7f3d0;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.25);
  animation: pulseDot 2s infinite;
}

@keyframes pulseDot {
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.8; }
}

/* 4 KPI Blocks Grid */
.kpi-sections-wrapper {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.metric-block {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px;
}

.metric-block-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 700;
  color: #334155;
  margin-bottom: 12px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.metric-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 12px;
  transition: all 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.metric-label {
  display: block;
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
}

.metric-val {
  font-size: 20px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 3px;
}

.metric-sub {
  font-size: 10.5px;
  color: #94a3b8;
  font-weight: 500;
}

.card-icon-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
}

.bg-orange-soft { background: #ffedd5; }
.bg-teal-soft { background: #ccfbf1; }
.bg-purple-soft { background: #f3e8ff; }
.bg-dark-soft { background: #f1f5f9; }

/* Border color hints */
.border-indigo { border-left: 3.5px solid #4f46e5; }
.border-green { border-left: 3.5px solid #10b981; }
.border-amber { border-left: 3.5px solid #f59e0b; }
.border-blue { border-left: 3.5px solid #0284c7; }

.border-emerald { border-left: 3.5px solid #059669; }
.border-yellow { border-left: 3.5px solid #d97706; }
.border-rose { border-left: 3.5px solid #ef4444; }
.border-slate { border-left: 3.5px solid #64748b; }

.border-orange { border-left: 3.5px solid #ea580c; }
.border-teal { border-left: 3.5px solid #0d9488; }
.border-purple { border-left: 3.5px solid #9333ea; }
.border-dark { border-left: 3.5px solid #334155; }

.border-cyan { border-left: 3.5px solid #06b6d4; }
.border-pink { border-left: 3.5px solid #ec4899; }
.border-red { border-left: 3.5px solid #e11d48; }
.border-violet { border-left: 3.5px solid #8b5cf6; }

/* Text colors */
.text-indigo { color: #4f46e5; }
.text-green { color: #10b981; }
.text-amber { color: #f59e0b; }
.text-blue { color: #0284c7; }
.text-emerald { color: #059669; }
.text-yellow { color: #d97706; }
.text-rose { color: #ef4444; }
.text-slate { color: #64748b; }
.text-orange { color: #ea580c; }
.text-teal { color: #0d9488; }
.text-purple { color: #9333ea; }
.text-dark { color: #334155; }
.text-cyan { color: #06b6d4; }
.text-pink { color: #ec4899; }
.text-red { color: #e11d48; }
.text-violet { color: #8b5cf6; }

.mt-2 { margin-top: 6px; }

/* Charts Layout */
.charts-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: -10px;
}

.charts-row::before,
.charts-row::after {
  display: none;
}

.charts-row > .el-col {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.chart-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  box-sizing: border-box;
}

.h-100 {
  height: 100%;
}

.chart-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.chart-title {
  margin: 0 0 3px;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.chart-subtitle {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

.tag-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 8px;
}

.tag-pill--green { background: #dcfce7; color: #15803d; }
.tag-pill--orange { background: #ffedd5; color: #c2410c; }

/* Scope tabs */
.scope-toggle-tabs {
  display: inline-flex;
  background: #e2e8f0;
  padding: 3px;
  border-radius: 10px;
}

.scope-btn {
  border: none;
  background: transparent;
  padding: 4px 10px;
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.scope-btn.active {
  background: #ffffff;
  color: #4f46e5;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
}

.chart-container {
  width: 100%;
  height: 240px;
  position: relative;
}

.chart-container--meal {
  height: 200px;
}

/* Gauge Footer */
.gauge-footer-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
}

.gauge-stat-box {
  text-align: center;
  background: #ffffff;
  padding: 8px 4px;
  border-radius: 10px;
  border: 1px solid #f1f5f9;
}

.g-label {
  display: block;
  font-size: 10.5px;
  color: #64748b;
  margin-bottom: 2px;
}

.g-val {
  font-size: 13px;
  font-weight: 800;
}

/* Trend Legend Bar */
.trend-legend-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-color-box {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.bar-color { background: #3b82f6; }
.bg-slate-color { background: #cbd5e1; }
.line-color {
  height: 3px;
  border-radius: 999px;
  background: #10b981;
}

/* Meal Detail List */
.meal-detail-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
}

.meal-detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #475569;
}

.meal-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
}

.meal-counts {
  font-size: 12px;
}

.meal-rate {
  font-weight: 700;
  margin-left: 4px;
}

/* 3. Class Attendance Section */
.class-attendance-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 20px 22px;
  margin-top: 24px;
}

.class-attendance-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
}

.class-attendance-head.is-expanded {
  margin-bottom: 18px;
}

.class-attendance-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.table-toggle-btn {
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.class-table-collapsible-body {
  margin-top: 16px;
}

.class-attendance-title-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.class-attendance-title-group .section-icon-badge {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #ede9fe;
  color: #6366f1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.section-heading {
  margin: 0 0 3px;
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
}

.section-subheading {
  margin: 0;
  font-size: 12.5px;
  color: #64748b;
}

.class-attendance-filter-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-chip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.chip-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.grade-select {
  width: 175px;
}

.search-box {
  width: 220px;
}

.class-counter-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  color: #4f46e5;
  background: #e0e7ff;
  padding: 5px 12px;
  border-radius: 8px;
}

.class-attendance-table-wrap {
  overflow-x: auto;
}

.modern-class-attendance-table {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
}

.class-meta-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.class-name-txt {
  font-size: 13.5px;
  color: #1e293b;
}

.grade-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
  display: inline-block;
  width: fit-content;
}

.pill-nursery { background: #fce7f3; color: #db2777; }
.pill-kinder_1 { background: #fef3c7; color: #b45309; }
.pill-kinder_2 { background: #dcfce7; color: #15803d; }
.pill-kinder_3 { background: #ede9fe; color: #7c3aed; }

.badge-total-students {
  font-weight: 700;
  color: #1e293b;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
}

.attendance-progress-cell {
  display: flex;
  justify-content: center;
}

.badge-done {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-weight: 700;
  color: #15803d;
  background: #dcfce7;
  padding: 3px 10px;
  border-radius: 999px;
}

.badge-pending {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-weight: 700;
  color: #b45309;
  background: #fef3c7;
  padding: 3px 10px;
  border-radius: 999px;
}

.rate-progress-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rate-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.rate-pct-tag {
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
}

.rate-good { background: #dcfce7; color: #15803d; }
.rate-mid { background: #fef3c7; color: #b45309; }
.rate-low { background: #fee2e2; color: #b91c1c; }

.progress-bar-bg {
  width: 100%;
  height: 6px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.absent-stat-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11.5px;
}

.stat-permit { color: #b45309; }
.stat-no-permit { color: #b91c1c; }

.meal-mini-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
}

.chip-m {
  font-size: 10.5px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.chip-breakfast { background: #fff7ed; color: #ea580c; border: 1px solid #ffedd5; }
.chip-lunch { background: #f0fdfa; color: #0d9488; border: 1px solid #ccfbf1; }
.chip-snack { background: #faf5ff; color: #7e22ce; border: 1px solid #f3e8ff; }
.chip-dinner { background: #f8fafc; color: #475569; border: 1px solid #e2e8f0; }

.leave-stat-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11.5px;
}

.leave-yes { color: #15803d; font-weight: 600; }
.leave-no { color: #64748b; }

/* Responsive */
@media (max-width: 1400px) {
  .kpi-sections-wrapper {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .kpi-sections-wrapper {
    grid-template-columns: 1fr;
  }
  .class-attendance-head {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
